import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Check, Star, Zap, Shield, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingPage = ({ onNavigate }) => {
  const { user, isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Fetch plans
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/payments/plans`)
      .then(res => res.json())
      .then(data => setPlans(data))
      .catch(() => {});
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempt) => {
    if (attempt >= 6) {
      setCheckingPayment(false);
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('Payment successful! You are now a PRO member!');
        setTimeout(() => window.location.reload(), 1500);
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2500);
    } catch {
      setCheckingPayment(false);
      toast.error('Error checking payment status.');
    }
  }, []);

  // Check for returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId && isAuthenticated) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId, 0);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isAuthenticated, pollPaymentStatus]);

  const handleSubscribe = async (packageId) => {
    if (!isAuthenticated) {
      toast.error('Please login first to subscribe');
      onNavigate('login');
      return;
    }

    setLoading(packageId);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BACKEND_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create checkout');
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(null);
    }
  };

  const isPro = user?.tier === 'PRO';

  const features = [
    'Unlimited floor plan projects',
    'Full 24 Mountains Luo Pan compass',
    'Flying Star chart overlay',
    'Personal Kua analysis',
    'All Five Elements markers',
    'Save & sync across devices',
    'Export high-quality images',
    'Priority support',
  ];

  return (
    <div data-testid="pricing-page" style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)', color: 'white', padding: '40px 20px' }}>
      {/* Back button */}
      <div style={{ maxWidth: '1100px', margin: '0 auto 24px' }}>
        <button
          data-testid="pricing-back-btn"
          onClick={() => onNavigate('home')}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '14px' }}
        >
          <ArrowLeft size={16} /> Back to Home
        </button>
      </div>

      {/* Header */}
      <div style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 48px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(234, 179, 8, 0.15)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: '20px', padding: '6px 16px', marginBottom: '16px' }}>
          <Star size={14} style={{ color: '#eab308' }} />
          <span style={{ fontSize: '13px', color: '#eab308', fontWeight: '500' }}>PRO Membership</span>
        </div>
        <h1 style={{ fontSize: '36px', fontWeight: '700', margin: '0 0 12px', lineHeight: '1.2' }}>
          Unlock Full Feng Shui Power
        </h1>
        <p style={{ fontSize: '16px', color: '#94a3b8', margin: 0 }}>
          Get unlimited access to all professional Feng Shui analysis tools
        </p>
      </div>

      {/* Payment checking overlay */}
      {checkingPayment && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#1e293b', borderRadius: '16px', padding: '40px', textAlign: 'center', border: '1px solid #334155' }}>
            <div style={{ width: '40px', height: '40px', border: '3px solid #334155', borderTop: '3px solid #eab308', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <h3 style={{ margin: '0 0 8px', color: 'white' }}>Verifying Payment...</h3>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '14px' }}>Please wait while we confirm your payment</p>
          </div>
        </div>
      )}

      {/* Pricing Cards */}
      <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', maxWidth: '800px', margin: '0 auto 48px', flexWrap: 'wrap' }}>
        {/* Quarterly Plan */}
        <div data-testid="plan-quarterly" style={{
          flex: '1', minWidth: '300px', maxWidth: '380px',
          background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '32px',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Zap size={20} style={{ color: '#3b82f6' }} />
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Quarterly</h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span style={{ fontSize: '42px', fontWeight: '700' }}>$119</span>
              <span style={{ color: '#64748b', fontSize: '14px' }}>/3 months</span>
            </div>
            <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0' }}>~$39.67/month</p>
          </div>

          <div style={{ flex: 1, marginBottom: '24px' }}>
            {features.slice(0, 6).map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', color: '#cbd5e1', fontSize: '14px' }}>
                <Check size={16} style={{ color: '#3b82f6', flexShrink: 0 }} /> {f}
              </div>
            ))}
          </div>

          <button
            data-testid="subscribe-quarterly-btn"
            onClick={() => handleSubscribe('quarterly')}
            disabled={loading || isPro}
            style={{
              width: '100%', padding: '14px', fontSize: '15px', fontWeight: '600',
              background: isPro ? '#334155' : '#3b82f6', color: 'white',
              border: 'none', borderRadius: '10px', cursor: isPro ? 'default' : 'pointer',
              opacity: loading === 'quarterly' ? 0.7 : 1,
            }}
          >
            {isPro ? 'Current Plan' : loading === 'quarterly' ? 'Redirecting...' : 'Get Quarterly'}
          </button>
        </div>

        {/* Annual Plan - Featured */}
        <div data-testid="plan-annual" style={{
          flex: '1', minWidth: '300px', maxWidth: '380px',
          background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
          border: '2px solid #eab308', borderRadius: '16px', padding: '32px',
          display: 'flex', flexDirection: 'column', position: 'relative',
        }}>
          {/* Best Value badge */}
          <div style={{
            position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #eab308, #f59e0b)', color: '#0f172a',
            padding: '4px 16px', borderRadius: '12px', fontSize: '12px', fontWeight: '700',
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            Best Value
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Shield size={20} style={{ color: '#eab308' }} />
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Annual</h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span style={{ fontSize: '42px', fontWeight: '700' }}>$399</span>
              <span style={{ color: '#64748b', fontSize: '14px' }}>/year</span>
            </div>
            <p style={{ color: '#eab308', fontSize: '13px', margin: '4px 0 0', fontWeight: '500' }}>Save $77 vs quarterly</p>
          </div>

          <div style={{ flex: 1, marginBottom: '24px' }}>
            {features.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', color: '#cbd5e1', fontSize: '14px' }}>
                <Check size={16} style={{ color: '#eab308', flexShrink: 0 }} /> {f}
              </div>
            ))}
          </div>

          <button
            data-testid="subscribe-annual-btn"
            onClick={() => handleSubscribe('annual')}
            disabled={loading || isPro}
            style={{
              width: '100%', padding: '14px', fontSize: '15px', fontWeight: '600',
              background: isPro ? '#334155' : 'linear-gradient(135deg, #eab308, #f59e0b)', color: isPro ? 'white' : '#0f172a',
              border: 'none', borderRadius: '10px', cursor: isPro ? 'default' : 'pointer',
              opacity: loading === 'annual' ? 0.7 : 1,
            }}
          >
            {isPro ? 'Current Plan' : loading === 'annual' ? 'Redirecting...' : 'Get Annual Plan'}
          </button>
        </div>
      </div>

      {/* Current plan status */}
      {isPro && user?.expiry_date && (
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'inline-block', background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: '12px', padding: '16px 24px' }}>
            <p style={{ margin: 0, color: '#eab308', fontWeight: '500' }}>
              You're a PRO member! Your plan expires on {new Date(user.expiry_date).toLocaleDateString()}
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default PricingPage;
